import {sameCurrency} from "@/features/pricing";
import {Session} from "../../../../../features/sessions";
import {GetSessionApiResponse} from "../../../../../react-query-hooks/session/types/edit-session";
import {CoverImagesItemResponse} from "../../../../../react-query-hooks/products/types";
import {BundleBookingIntervals} from "../../../../../react-query-hooks/session/types/bundle/create";

/**
 * Adapter that converts Session entity to GetSessionApiResponse format.
 *
 * This is a temporary compatibility layer for Phase 2 of the V2 UI migration.
 * It allows consumers to continue using the legacy API response shape while
 * the context uses the new Session entity internally.
 *
 * TODO: Remove in Phase 4 when consumers are updated to use Session entity directly.
 */
export function sessionToLegacyAdapter(session: Session): GetSessionApiResponse {
  const props = session.toProps();

  return {
    // Base fields (camelCase in both)
    id: props.id,
    userId: props.userId,
    name: props.name,
    slug: props.slug,
    description: props.description,
    callDuration: props.callDuration,
    callPrice: props.callPrice,
    callPriceCurrency: props.callPriceCurrency,
    minCallPrice: props.minCallPrice,
    isPayWhatYouWant: props.isPayWhatYouWant,
    videoLocation: props.videoLocation,
    audioLocation: props.audioLocation,
    videoPlatform: props.videoPlatform ?? "",
    hideServiceFromMenu: props.hideServiceFromMenu,
    hasGoogleMeet: props.hasGoogleMeet,
    statusName: props.statusName,
    createdAt: props.createdAt,
    updatedAt: props.updatedAt,

    // Snake_case fields expected by legacy consumers
    status_id: props.statusId,
    cover_picture: props.coverPicture,
    cover_images: adaptCoverImages(props.id, props.userId, props.coverImages),
    url_redirect: props.urlRedirect || null,
    redirectType: props.redirectType,
    telegramGroupChatId: props.telegramGroupChatId,
    request_phone_number: props.requestPhoneNumber,
    tiny_url: props.tinyUrl,
    enable_currency_conversion: props.enableCurrencyConversion,

    // Currencies with legacy shape
    currencies: props.currencies.map((c, index) => ({
      id: index + 1, // Generate placeholder ID for form compatibility
      userId: props.userId,
      service_id: props.id,
      service_type: 1,
      currency: c.currency,
      amount: c.amount,
      minCallPrice: c.minCallPrice,
      is_auto_converted: c.isAutoConverted ?? false,
      is_base:
        c.currency.toUpperCase() === props.callPriceCurrency.toUpperCase() ||
        (!props.callPriceCurrency && c.isAutoConverted === false),
      createdAt: props.createdAt,
      updatedAt: props.updatedAt,
    })),

    // Revenue (empty array - not available in Session entity)
    revenue: [],

    // Booking questions with snake_case answer_type
    bookingQuestions: props.bookingQuestions.map((q) => ({
      question: q.question,
      required: q.required,
      answer_type: q.answerType,
      options: q.options ?? [],
    })),

    // Schedule (renamed from availabilitySchedule)
    schedule: props.availabilitySchedule
      ? {
          id: props.availabilitySchedule.id,
          name: props.availabilitySchedule.name,
          sessionCount: props.availabilitySchedule.sessionCount,
          isDefault: props.availabilitySchedule.isDefault,
          createdAt: props.createdAt,
          updatedAt: props.updatedAt,
          userId: String(props.userId),
        }
      : {
          id: "",
          name: "",
          sessionCount: 0,
          isDefault: false,
          createdAt: props.createdAt,
          updatedAt: props.updatedAt,
          userId: String(props.userId),
        },

    // Session/bundle discriminator fields
    booking_interval: props.bookingInterval as BundleBookingIntervals | null,
    is_bundle: props.isBundle,
    no_of_sessions: props.numberOfSessions,
  } as GetSessionApiResponse;
}

/**
 * Converts SessionCoverImage[] to CoverImagesItemResponse[] format.
 * The Session entity stores cover images with id and url, legacy consumers
 * expect objects with id, media_url, and other properties.
 */
function adaptCoverImages(
  offeringId: number,
  userId: number,
  coverImages: {id: string; url: string}[]
): CoverImagesItemResponse[] {
  const now = new Date().toISOString();
  return coverImages.map((img) => ({
    id: img.id,
    media_url: img.url,
    media_type: "image" as const,
    offering_id: offeringId,
    offering_type: "service" as const,
    user_id: userId,
    createdAt: now,
    updatedAt: now,
  }));
}
